"use client";

import { useCallback, useMemo, useState } from "react";
import type { CanvasVariant, VariantAxis } from "src/components/canvas/variant-resolver";
import {
  changeFinish,
  closestBySize,
  finishOf,
  variantsOfFinish,
  type Finish,
  type FinishAxis,
} from "src/components/canvas/finish-options";
import { variantDisplayPrice } from "src/components/canvas/variant-price";
import { useShopCurrencyValue } from "src/hooks/useShopMarket";
import { offeredSizes } from "./artwork-sizing";
import {
  artworkSizeGroups,
  entryVariantId,
  variantAxesOf,
} from "./artwork-purchase";
import { useCanvasVariants } from "./useArtwork";
import type { Artwork } from "./artwork-types";

/**
 * Finish and size for one work, as the artwork page and the quick-add sheet
 * both ask them: only combinations this master can honestly be printed as,
 * the finish read off the chosen size so the two never disagree, and a
 * finish change that keeps the size when it can.
 *
 * Both open on the shop's standard finish at its entry size (entryVariantId),
 * the size the cards quote their prices at.
 */
export function useArtworkOptions(
  artwork: Pick<Artwork, "widthPx" | "heightPx"> | null | undefined,
) {
  // Sizes come from the canvas product: an artwork is the same physical thing
  // as a photo canvas, printed from a different source. The same cached read
  // the gallery makes, so arriving from it costs no request.
  const { data: variantRows, isError: variantsFailed } = useCanvasVariants();
  const variants = useMemo<CanvasVariant[] | null>(
    () => (variantRows ? variantRows : variantsFailed ? [] : null),
    [variantRows, variantsFailed],
  );
  const axes = useMemo<VariantAxis[]>(() => variantAxesOf(variantRows), [variantRows]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Everything this master can honestly be printed as, before the finish narrows it.
  const printable = useMemo(() => {
    if (!artwork || !variants) return [];
    return offeredSizes({ widthPx: artwork.widthPx, heightPx: artwork.heightPx }, variants);
  }, [artwork, variants]);
  const printableVariants = useMemo(() => printable.map((p) => p.variant), [printable]);

  // Derived rather than set in an effect, so the first render already shows it.
  const defaultId = useMemo(() => entryVariantId(printable, axes), [printable, axes]);

  // The customer's pick while it is still sellable, the opening one otherwise.
  const selected =
    printable.find((p) => p.variant.id === selectedId) ??
    printable.find((p) => p.variant.id === defaultId) ??
    null;
  const finish = useMemo<Finish>(() => finishOf(selected?.variant), [selected]);

  /**
   * A new finish keeps the size the customer picked when that finish has it,
   * and otherwise moves to the nearest one. Paper cannot be stretched; someone
   * who clicks Paper while "stretched" is chosen means Paper, so the frame
   * follows rather than the click being refused.
   */
  const changeFinishTo = useCallback(
    (key: FinishAxis, value: string) => {
      const next = changeFinish(printableVariants, axes, finish, key, value);
      const match = closestBySize(variantsOfFinish(printableVariants, next), selected?.variant);
      if (match) setSelectedId(match.id);
    },
    [printableVariants, axes, finish, selected],
  );

  const groups = useMemo(
    () => artworkSizeGroups(printableVariants, finish),
    [printableVariants, finish],
  );

  /**
   * Price in the currency of the domain being browsed, as an amount and a
   * currency that travel together — a market without its own price for a size
   * is shown the euro figure as euros, never under its own symbol.
   */
  const currency = useShopCurrencyValue();
  const price = selected ? variantDisplayPrice(selected.variant, currency) : null;

  return {
    variants,
    axes,
    printable,
    printableVariants,
    selected,
    select: setSelectedId,
    finish,
    changeFinishTo,
    groups,
    currency,
    price,
  };
}
